<script lang="ts">
  // Swipe-to-confirm affordance per INTERFACE-SPECIFICATION.md §12.7.
  //
  // The user drags the handle from left to right; releasing past the
  // commit threshold fires onConfirm. Used for irreversible-feeling
  // operations (full rollback, draft discard, proposal discard). A
  // swipe gesture removes the failure mode where a single tap on a
  // touch device commits a destructive operation by accident.
  //
  // Pointer events unify touch, mouse, and pen so the same affordance
  // works on every input device the maintenance interface might see.
  // Keyboard activation (Enter / Space on the handle) commits without
  // requiring a swipe; the visible label communicates that the
  // affordance is interactive.
  //
  // The component is presentational — it has no opinion on what
  // happens after onConfirm fires. Callers handle their own busy
  // state via `confirming`; the handle disables and the label
  // changes to confirmingLabel while the work runs.

  interface Props {
    label: string;
    confirmingLabel?: string;
    disabled?: boolean;
    confirming?: boolean;
    onConfirm: () => void | Promise<void>;
    // Visual register. 'danger' uses the validation-error red fill;
    // 'neutral' uses the action-primary fill. Default is 'danger'
    // because the spec-named callers (full rollback, draft discard,
    // proposal discard) are all destructive.
    variant?: 'danger' | 'neutral';
  }

  const {
    label,
    confirmingLabel = 'Working…',
    disabled = false,
    confirming = false,
    onConfirm,
    variant = 'danger'
  }: Props = $props();

  // Threshold the handle must cross (as a fraction of the track
  // width) before release commits. 0.8 leaves a small buffer so
  // a deliberate-feeling swipe lands; lower values fire too
  // eagerly on long-finger drags.
  const COMMIT_THRESHOLD = 0.8;

  let trackEl: HTMLDivElement | null = $state(null);
  let dragging = $state<boolean>(false);
  let progress = $state<number>(0);
  let startX = 0;
  let trackWidth = 0;
  let handleWidth = 0;
  let activePointerId: number | null = null;

  function onPointerDown(event: PointerEvent) {
    if (disabled || confirming) return;
    if (!trackEl) return;
    activePointerId = event.pointerId;
    dragging = true;
    startX = event.clientX;
    trackWidth = trackEl.clientWidth;
    // Approximate handle width so the handle never overshoots the
    // right edge; reads off the rendered DOM so changes to padding
    // and border are absorbed automatically.
    const handle = trackEl.querySelector<HTMLElement>('.swipe-confirm__handle');
    handleWidth = handle ? handle.offsetWidth : 0;
    (event.currentTarget as Element).setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  function onPointerMove(event: PointerEvent) {
    if (!dragging) return;
    if (activePointerId !== null && event.pointerId !== activePointerId) return;
    const maxTravel = Math.max(1, trackWidth - handleWidth);
    const dx = event.clientX - startX;
    progress = Math.min(1, Math.max(0, dx / maxTravel));
  }

  async function onPointerUp() {
    if (!dragging) return;
    dragging = false;
    activePointerId = null;
    if (progress >= COMMIT_THRESHOLD) {
      // Commit. Caller takes over (its confirming flag will set the
      // handle disabled until the work finishes). Snap to full so
      // the visual stays parked at the end of the track during the
      // async work — releasing before the threshold snaps back.
      progress = 1;
      await Promise.resolve(onConfirm());
    } else {
      progress = 0;
    }
  }

  function onKey(event: KeyboardEvent) {
    if (disabled || confirming) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      progress = 1;
      void Promise.resolve(onConfirm());
    }
  }

  const fillPercent = $derived(Math.round(progress * 100));
</script>

<div
  class="swipe-confirm"
  class:is-disabled={disabled || confirming}
  class:is-confirming={confirming}
  data-variant={variant}
>
  <div
    class="swipe-confirm__track"
    bind:this={trackEl}
    onpointerdown={onPointerDown}
    onpointermove={onPointerMove}
    onpointerup={onPointerUp}
    onpointercancel={onPointerUp}
    role="button"
    tabindex={disabled || confirming ? -1 : 0}
    aria-label={confirming ? confirmingLabel : label + ' (swipe right to confirm, or press Enter)'}
    aria-disabled={disabled || confirming}
    onkeydown={onKey}
  >
    <div
      class="swipe-confirm__fill"
      style="width: {fillPercent}%"
      aria-hidden="true"
    ></div>
    <span class="swipe-confirm__label">
      {confirming ? confirmingLabel : label}
    </span>
    <span class="swipe-confirm__handle" aria-hidden="true">→</span>
  </div>
  <p class="swipe-confirm__hint muted">
    Swipe the handle to confirm. Cannot be undone.
  </p>
</div>

<style>
  .swipe-confirm { display: flex; flex-direction: column; gap: var(--space-1); width: 100%; }
  .swipe-confirm__track {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: var(--touch-target-min);
    border: 1px solid var(--surface-line);
    border-radius: var(--radius-md);
    background: var(--surface-card);
    overflow: hidden;
    cursor: grab;
    user-select: none;
    touch-action: none;
  }
  .swipe-confirm__track:focus-visible {
    outline: 2px solid var(--input-interactive);
    outline-offset: 2px;
  }
  .swipe-confirm.is-disabled .swipe-confirm__track {
    cursor: not-allowed;
    opacity: 0.5;
  }
  .swipe-confirm__fill {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    background: var(--validation-error-bg);
    transition: width 80ms ease-out;
    z-index: 0;
  }
  .swipe-confirm[data-variant="danger"] .swipe-confirm__fill {
    background: var(--validation-error-bg);
  }
  .swipe-confirm[data-variant="neutral"] .swipe-confirm__fill {
    background: var(--surface-overlay);
  }
  .swipe-confirm__label {
    position: relative;
    z-index: 1;
    font-weight: 600;
    color: var(--surface-fg);
  }
  .swipe-confirm__handle {
    position: absolute;
    left: var(--space-2);
    top: 50%;
    transform: translateY(-50%);
    width: 2.5rem;
    height: 2.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--action-primary);
    color: var(--action-primary-fg);
    border-radius: var(--radius-sm);
    font-weight: 700;
    z-index: 2;
    pointer-events: none;
  }
  .swipe-confirm[data-variant="danger"] .swipe-confirm__handle {
    background: var(--validation-error);
  }
  .swipe-confirm__hint { margin: 0; font-size: var(--type-xs); }
</style>
